import React from 'react';
import { Star, XCircle, Clock, Leaf, UserCheck, Camera } from 'lucide-react';

const OfferingsSection = () => {
  const offerings = [
    {
      title: 'Ride Now',
      shortDescription: 'Need a quick ride? Book & Ride Now',
      cta: 'Book your ride now',
      gridImage: 'https://customer-assets.emergentagent.com/job_green-ride-2/artifacts/ehrlfugt_Ride%20Now.png',
    },
    {
      title: 'Auto-mate',
      shortDescription: 'Enjoy the feeling of owning an Auto with monthly subscription plans',
      cta: 'Set up your daily ride',
      gridImage: 'https://customer-assets.emergentagent.com/job_green-ride-2/artifacts/i25njqk3_Auto%20mate.png',
    },
    {
      title: 'Hop & Go',
      shortDescription: 'Simply hop & go wherever you need.',
      cta: 'Find a ride near you',
      gridImage: 'https://customer-assets.emergentagent.com/job_green-ride-2/artifacts/3v0zea4x_Hop%20%26%20Go.png',
    },
  ];

  const highlights = [
    {
      icon: <XCircle size={28} />,
      title: 'Zero Cancellations',
      description: 'Guaranteed rides with no last-minute drop-offs.',
    },
    {
      icon: <Clock size={28} />,
      title: '5-Minute Pickups',
      description: 'Quick and reliable pickups for everyday city travel.',
    },
    {
      icon: <Leaf size={28} />,
      title: 'Zero Emissions',
      description: '100% electric rides for cleaner, greener cities.',
    },
    {
      icon: <UserCheck size={28} />,
      title: 'Verified Pilots',
      description: 'Trained and verified drivers you can trust.',
    },
    {
      icon: <Camera size={28} />,
      title: 'Dashcam Enabled',
      description: 'In-ride dashcams for added passenger safety.',
    },
  ];

  const testimonials = [
    {
      name: 'Suresh K',
      role: 'Parent',
      location: 'Chennai',
      review: "The rides are punctual and safe. My child is comfortable, and I always know where the ride is. That matters the most.",
    },
    {
      name: 'Meena S',
      role: 'Office Commuter',
      location: 'Chennai',
      review: "Nura has become my everyday ride. It's reliable, clean, and I never have to worry about last-minute cancellations.",
    },
    {
      name: 'Rahul P',
      role: 'College Student',
      location: 'Chennai',
      review: "It's affordable and convenient for daily travel. The electric autos are smooth, and booking is simple.",
    },
  ];

  // Detect user's OS
  const getAppStoreLink = () => {
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;

    // iOS -> App Store
    if (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream) {
      return 'https://apps.apple.com/az/app/nura-mobility/id6749004862';
    }

    // Android -> Play Store
    if (/android/i.test(userAgent)) {
      return 'https://play.google.com/store/apps/details?id=in.co.nuraemobility.nuraApplication';
    }

    // Mac -> App Store
    if (/Macintosh|MacIntel|MacPPC|Mac68K/.test(userAgent)) {
      return 'https://apps.apple.com/az/app/nura-mobility/id6749004862';
    }

    // Windows + everything else -> Play Store
    return 'https://play.google.com/store/apps/details?id=in.co.nuraemobility.nuraApplication';
  };

  const handleCtaClick = () => {
    const appStoreLink = getAppStoreLink();
    window.open(appStoreLink, '_blank', 'noopener,noreferrer');
  };

  return (
    <section id="offerings" className="py-4 sm:py-6 bg-black">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-6 sm:mb-8">
          <h2 className="text-2xl sm:text-3xl font-normal text-white">
            Choose Your Ride
          </h2>
        </div>

        {/* 4x1 Grid View */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-7xl mx-auto mb-16">
          {offerings.map((offering, index) => (
            <div
              key={index}
              className="bg-[#bceb39] rounded-3xl p-6 sm:p-8 shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 flex flex-col"
            >
              <h3 className="text-2xl sm:text-3xl font-bold mb-4 text-black">{offering.title}</h3>

              <div className="overflow-hidden rounded-xl mb-6 aspect-[16/10]">
                <img
                  src={offering.gridImage}
                  alt={offering.title}
                  className="w-full h-full object-cover hover:scale-110 transition-transform duration-500"
                />
              </div>

              <p className="text-base sm:text-lg text-gray-800 leading-relaxed mb-6 flex-grow">
                {offering.shortDescription}
              </p>

              <button
                onClick={handleCtaClick}
                className="bg-black text-white px-6 py-3 rounded-full font-semibold hover:bg-gray-800 transition-all duration-300 transform hover:scale-105 w-full"
              >
                {offering.cta}
              </button>
            </div>
          ))}
        </div>

        {/* Highlight Boxes - 5 in a row */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-5 mb-16">
          {highlights.map((highlight, index) => (
            <div
              key={index}
              className="bg-gray-900 rounded-xl p-6 border border-gray-800 text-center shadow-md"
            >
              <div className="w-14 h-14 bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-4 text-[#bceb39]">
                {highlight.icon}
              </div>
              <h3 className="text-lg font-bold text-white mb-2">{highlight.title}</h3>
              <p className="text-gray-400 text-sm">{highlight.description}</p>
            </div>
          ))}
        </div>

        {/* Testimonials Section - Static Grid */}
        <div>
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-3">
            Voices of Our <span className="text-[#bceb39]">Happy Riders</span>
          </h2>
          <p className="text-lg text-gray-400 mb-10">
            Real experiences from real people
          </p>

          {/* 2x2 Grid on desktop, 1 column on mobile */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {testimonials.map((testimonial, index) => (
              <div
                key={index}
                className="bg-gray-900 rounded-xl p-6 border border-gray-800"
              >
                {/* Stars */}
                <div className="flex gap-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} size={18} className="text-[#bceb39] fill-[#bceb39]" />
                  ))}
                </div>

                {/* Review Text */}
                <p className="text-gray-300 text-base leading-relaxed mb-5">
                  "{testimonial.review}"
                </p>

                {/* User Info */}
                <div className="border-t border-gray-800 pt-4">
                  <p className="text-white font-semibold">{testimonial.name}</p>
                  <p className="text-[#bceb39] text-sm">{testimonial.role}</p>
                  <p className="text-gray-500 text-sm">{testimonial.location}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default OfferingsSection;
